import type { BotDifficulty } from '@signalclash/shared';
import { DEFAULT_BOT_DIFFICULTY } from '@signalclash/shared';

// Settings: persisted client-side preferences. Audio volumes are also persisted
// directly by AudioBus (those localStorage keys are independent — AudioBus owns
// the audio side); this module owns presentation-only settings that don't have
// a natural home elsewhere. V1 stores camera scroll speed; V1.5 adds the
// player's chosen bot difficulty (Recruit / Tactical / Commander) so a fresh
// browser session picks up where the player left off.
//
// Storage key:    signalclash.settings.v1
// Versioning:     bump the suffix when the schema changes; a missing/invalid
//                 entry falls back to defaults silently.

const STORAGE_KEY = 'signalclash.settings.v1';

// Px/sec when the player holds an arrow key. Scaled down by zoom inside
// InputRouter so the *visible* travel speed is constant regardless of camera
// zoom level. Range chosen so the slowest setting is still usable on a desktop
// (≈half a screen-width per second at 1× zoom) and the fastest is fast enough
// for a hectic match without overshooting.
export const SCROLL_SPEED_MIN_PX_PER_SEC = 400;
export const SCROLL_SPEED_MAX_PX_PER_SEC = 2400;
export const SCROLL_SPEED_DEFAULT_PX_PER_SEC = 1200;

const VALID_BOT_DIFFICULTIES: ReadonlySet<BotDifficulty> = new Set<BotDifficulty>([
  'recruit',
  'tactical',
  'commander',
]);

export interface ClientSettings {
  scrollSpeedPxPerSec: number;
  botDifficulty: BotDifficulty;
  // Portal requirement: players can disable anonymous match-started analytics.
  // Defaults to true (opted-in). Local (bot) play never fires analytics regardless;
  // this flag only affects the network-mode sendBeacon call.
  analyticsEnabled: boolean;
}

export const DEFAULT_SETTINGS: ClientSettings = {
  scrollSpeedPxPerSec: SCROLL_SPEED_DEFAULT_PX_PER_SEC,
  botDifficulty: DEFAULT_BOT_DIFFICULTY,
  analyticsEnabled: true,
};

export function loadSettings(): ClientSettings {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<ClientSettings>;
    return {
      scrollSpeedPxPerSec: clampScrollSpeed(
        parsed.scrollSpeedPxPerSec ?? DEFAULT_SETTINGS.scrollSpeedPxPerSec,
      ),
      botDifficulty: clampBotDifficulty(parsed.botDifficulty),
      analyticsEnabled: coerceBool(parsed.analyticsEnabled, DEFAULT_SETTINGS.analyticsEnabled),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: ClientSettings): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage full / disabled / private mode — settings stay session-local.
  }
}

export function clampScrollSpeed(v: number): number {
  if (Number.isNaN(v)) return SCROLL_SPEED_DEFAULT_PX_PER_SEC;
  if (v < SCROLL_SPEED_MIN_PX_PER_SEC) return SCROLL_SPEED_MIN_PX_PER_SEC;
  if (v > SCROLL_SPEED_MAX_PX_PER_SEC) return SCROLL_SPEED_MAX_PX_PER_SEC;
  return v;
}

export function clampBotDifficulty(v: unknown): BotDifficulty {
  if (typeof v === 'string' && VALID_BOT_DIFFICULTIES.has(v as BotDifficulty)) {
    return v as BotDifficulty;
  }
  return DEFAULT_BOT_DIFFICULTY;
}

function coerceBool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  return fallback;
}

// Centralized broadcaster so InputRouter / camera consumers can react to a
// settings change without round-tripping through ClientApp's lifecycle.
type Listener = (settings: ClientSettings) => void;

export class SettingsStore {
  private current: ClientSettings;
  private listeners = new Set<Listener>();

  constructor(initial?: ClientSettings) {
    this.current = initial ?? loadSettings();
  }

  get(): ClientSettings {
    return this.current;
  }

  set(next: Partial<ClientSettings>): void {
    this.current = {
      ...this.current,
      ...next,
      scrollSpeedPxPerSec:
        next.scrollSpeedPxPerSec !== undefined
          ? clampScrollSpeed(next.scrollSpeedPxPerSec)
          : this.current.scrollSpeedPxPerSec,
      botDifficulty:
        next.botDifficulty !== undefined
          ? clampBotDifficulty(next.botDifficulty)
          : this.current.botDifficulty,
      analyticsEnabled:
        next.analyticsEnabled !== undefined
          ? next.analyticsEnabled
          : this.current.analyticsEnabled,
    };
    saveSettings(this.current);
    // Same error-containment pattern as the snapshot fan-out: one bad
    // listener (e.g. an InputRouter scroll-speed apply that fails on a
    // disposed camera) must not stop the other subscribers.
    for (const fn of this.listeners) {
      try {
        fn(this.current);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[settings listener] threw', err);
      }
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
